import { useState } from 'react';
import { Menu, MenuButton, MenuList, Portal, useDisclosure } from '@chakra-ui/react';
import Button from '../../Button/Button';
import MenuItem from '../../Menu/MenuItem';
import Tooltip from '../../Tooltip/Tooltip';
import { useFilterContext } from '../Filter.context';
import FilterForm from '../FilterForm/FilterForm';
import { FilterValue } from '../Filter.types';
import { getMissingDependencies, hasAllDependencies } from '../Filter.utils';

export interface FilterAddProps {
  onChange: (category: string, selected: FilterValue) => void;
}

const FilterAdd = (props: FilterAddProps) => {
  const { onChange } = props;
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();

  const { isOpen, onClose: closeMenu, onOpen: openMenu } = useDisclosure();

  const { data, filtersDependOn, isLoading, setPopoverOpen, state } = useFilterContext();

  const onCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };

  const onOpen = () => {
    openMenu();
    setPopoverOpen(true);
    setSelectedCategory(undefined);
  };

  const onClose = () => {
    setPopoverOpen(false);
    closeMenu();
    setSelectedCategory(undefined);
  };

  const onFilterChange = (category: string, value: FilterValue) => {
    onClose();
    onChange(category, value);
  };

  const stateKeys = Object.keys(state);

  const categoryList = Object.keys(data).filter((category) => {
    return !stateKeys.includes(category) && data[category].type === 'tag';
  });

  const isDisabled = !hasAllDependencies(stateKeys, filtersDependOn) || categoryList.length === 0;

  return (
    <Menu closeOnSelect={false} isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
      <MenuButton
        as={Button}
        isDisabled={isDisabled}
        isLoading={isLoading}
        leftIconName="Plus"
        position={isOpen ? 'relative' : undefined}
        size="sm"
        variant="tertiary"
        zIndex={isOpen ? 'dialog' : undefined}
      >
        Add filter {isOpen}
      </MenuButton>
      <Portal>
        <MenuList
          paddingY={selectedCategory ? 0 : '12'}
          position={isOpen ? 'relative' : undefined}
          zIndex={isOpen ? 'dialog' : undefined}
        >
          {selectedCategory ? (
            <FilterForm
              category={selectedCategory}
              categoryName={data[selectedCategory].categoryName}
              onCancel={onClose}
              onChange={onFilterChange}
            />
          ) : (
            categoryList.map((category) => {
              const { categoryName, dependsOn } = data[category];
              const missingDependencies = getMissingDependencies(stateKeys, Object.keys(dependsOn || []));
              const isCategoryDisabled = missingDependencies.length > 0 || !data[category]?.options?.length;
              return (
                <Tooltip
                  key={category}
                  isDisabled={!isCategoryDisabled}
                  label={dependsOn?.[missingDependencies[0]] || 'There is no options for this category.'}
                  placement="right"
                >
                  <MenuItem
                    isDisabled={isCategoryDisabled}
                    onClick={() => onCategorySelect(category)}
                    pointerEvents="all"
                    rightIconName="ChevronRight"
                  >
                    {categoryName || category}
                  </MenuItem>
                </Tooltip>
              );
            })
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default FilterAdd;
